// src/pages/Users/Galleries/components/AlbumPhotoCard.jsx

import React from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaArrowsAlt,
  FaEye,
  FaRegStar,
  FaStar,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../services/albumPhotoServices";
import {
  FALLBACK_ALBUM_IMG,
  stripHtml,
} from "../utils/galleriesUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_ALBUM_IMG;
  }
}

export default function AlbumPhotoCard({
  photo,
  index,
  total,
  isManager,
  moving,
  isDragging,
  isOver,
  onOpen,
  onToggleCover,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) {
  const imageUrl =
    toAbsolutePhotoUrl(photo?.photo_url || "") || FALLBACK_ALBUM_IMG;

  const caption = stripHtml(photo?.description || "");
  const isCover = !!photo?.is_cover;

  return (
    <article
      draggable={isManager && !moving}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={[
        "group relative overflow-hidden rounded-[1.5rem] border bg-white shadow-sm transition dark:bg-slate-900",
        isCover
          ? "border-amber-300 ring-2 ring-amber-300 dark:border-amber-500 dark:ring-amber-500"
          : "border-slate-200 dark:border-slate-800",
        isDragging ? "opacity-60" : "",
        isOver ? "scale-[0.98] ring-2 ring-sky-400" : "",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={onOpen}
        className="relative block w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-950"
        aria-label="Voir la photo"
      >
        <div className="aspect-[4/3] w-full">
          <img
            src={imageUrl}
            alt={caption || `Photo ${index + 1}`}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
            loading="lazy"
            onError={onImgError}
          />
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />

        <div className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-950 shadow-sm">
          <FaEye />
        </div>

        <div className="absolute bottom-3 left-3 rounded-full bg-slate-950/75 px-3 py-1 text-xs font-bold text-white backdrop-blur">
          #{index + 1}
        </div>
      </button>

      {caption && (
        <div className="px-3 py-2">
          <p className="line-clamp-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
            {caption}
          </p>
        </div>
      )}

      {isManager && (
        <div className="border-t border-slate-200 p-2 dark:border-slate-800">
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onToggleCover}
              className={`inline-flex min-h-9 items-center justify-center rounded-xl text-xs font-bold transition active:scale-95 ${
                isCover
                  ? "bg-amber-400 text-amber-950"
                  : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              }`}
              title={
                isCover
                  ? "Retirer la couverture"
                  : "Définir comme couverture"
              }
            >
              {isCover ? <FaStar /> : <FaRegStar />}
            </button>

            <button
              type="button"
              onClick={onMoveUp}
              disabled={moving || index <= 0}
              className="inline-flex min-h-9 items-center justify-center rounded-xl bg-slate-100 text-xs font-bold text-slate-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-800 dark:text-slate-200"
              title="Monter la photo"
            >
              <FaArrowUp />
            </button>

            <button
              type="button"
              onClick={onMoveDown}
              disabled={moving || index >= total - 1}
              className="inline-flex min-h-9 items-center justify-center rounded-xl bg-slate-100 text-xs font-bold text-slate-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-800 dark:text-slate-200"
              title="Descendre la photo"
            >
              <FaArrowDown />
            </button>
          </div>

          <div className="mt-2 hidden items-center justify-center gap-1 rounded-xl bg-slate-50 px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:bg-slate-950 sm:flex">
            <FaArrowsAlt />
            Glisser
          </div>
        </div>
      )}
    </article>
  );
}